import { useCallback, useEffect, useState } from 'react';

import { useLazyGetBranchPreBlockWarningQuery } from '@app/api/Branches';
import { useBillingStatus, useUserPrint } from '@app/hooks';

type WarningStatus = 'pre-block' | 'ai-warning';

// Сумма задолженности и дата блокировки меняются редко, поэтому текст
// предупреждения кэшируется в SessionStorage на сутки (на филиал и статус),
// чтобы не дёргать GetBranchPreBlockWarning на каждый показ баннера.
const CACHE_KEY = 'bc-billing-preblock-warning';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedWarning {
  text: string;
  fetchedAt: number;
  branchId: number;
  status: WarningStatus;
}

const readCachedWarning = (
  branchId: number,
  status: WarningStatus
): string | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<CachedWarning>;
    const isFresh =
      typeof parsed?.text === 'string' &&
      typeof parsed?.fetchedAt === 'number' &&
      parsed.branchId === branchId &&
      parsed.status === status &&
      Date.now() - parsed.fetchedAt < CACHE_TTL_MS;

    return isFresh ? (parsed.text as string) : null;
  } catch {
    return null;
  }
};

const writeCachedWarning = (value: CachedWarning) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // SessionStorage может быть недоступен — предупреждение просто не кэшируется
  }
};

// Статусы "pre-block" (предупреждение о скорой блокировке за задолженность) и
// "ai-warning" (баланс филиала ушёл в минус, но ИИ-функции ещё работают)
// оба показывают предупреждающий баннер с текстом от GetBranchPreBlockWarning.
export const useBranchPreBlockWarning = () => {
  const { activeBranchId } = useUserPrint();
  const status = useBillingStatus();
  const [text, setText] = useState<string | null>(null);
  const [triggerGetWarning] = useLazyGetBranchPreBlockWarningQuery();

  const loadWarning = useCallback(
    async (branchId: number, warningStatus: WarningStatus) => {
      const cached = readCachedWarning(branchId, warningStatus);
      if (cached) {
        setText(cached);
        return;
      }

      try {
        const warning = await triggerGetWarning(
          { ActiveBranchId: branchId },
          true
        ).unwrap();

        if (typeof warning === 'string' && warning.trim()) {
          setText(warning);
          writeCachedWarning({
            text: warning,
            fetchedAt: Date.now(),
            branchId,
            status: warningStatus,
          });
        }
      } catch {
        // При ошибке получения текста баннер не показываем
      }
    },
    [triggerGetWarning]
  );

  const isWarningStatus = status === 'pre-block' || status === 'ai-warning';

  useEffect(() => {
    if (!isWarningStatus || activeBranchId == null) {
      setText(null);
      return;
    }

    loadWarning(activeBranchId, status as WarningStatus);
  }, [status, isWarningStatus, activeBranchId, loadWarning]);

  return { showWarning: isWarningStatus, text };
};
